import styled from '@emotion/styled';
import React, { memo, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  Row,
  Cell,
  Table as ReactTable,
} from '@tanstack/react-table';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import TableHeader from './TableHeader';
import { Empty, useTectonTheme } from '.';
import { EmptyProps } from './Empty';

export interface TableSortType<T> {
  predicate?: (items: T[], direction: 'ascending' | 'descending') => T[];
  direction: 'ascending' | 'descending';
  sortId: keyof T;
}

export type TableColumnWidth = 'xs' | 's' | 'm' | 'l' | 'xl';

export interface ExtendedTableColumn<T> {
  id?: unknown;
  name: string | React.ReactNode;
  sortId?: keyof T;
  tooltip?: string;
  isSortActive?: boolean;
  sortDirection?: 'ascending' | 'descending';
  align?: 'left' | 'center' | 'right';
  sortPredicate?: (items: T[], direction: 'ascending' | 'descending') => T[];
  sortCallback?: (direction: 'ascending' | 'descending') => void;
  hideCallback?: () => void;
  showCallback?: () => void;
  columnsContent?: React.ReactNode;
  filterContent?: React.ReactNode;
  content: (_: T) => React.ReactNode;
  isActive?: boolean;
  setIsActive?: (value: boolean) => void;
  columnWidth?: TableColumnWidth;
}

interface TableProps<T extends { id: string }> {
  items: T[];
  columns: ExtendedTableColumn<T>[];
  frozenFirstColumn?: boolean;
  compressed?: boolean;
  emptyProps?: EmptyProps;
  testId?: string;
  memoized?: boolean;
}

const TableWrapper = styled.div<{ frozenFirstColumn?: boolean; compressed?: boolean }>`
  user-select: none;
  overflow: hidden;
  position: relative;
  height: 100%;
  width: 100%;
  will-change: transform;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;

  .table-row {
    background-color: ${({ theme }) => theme.v1.colors.background.empty};
    display: flex;
    width: 100%;
    min-width: 100%;
    position: relative;

    &:hover {
      .table-cell {
        background-color: ${({ theme }) => theme.v1.colors.background.hover};
      }
    }

    min-height: ${({ theme, compressed }) => (compressed ? '0' : theme.v1.size['12'])}px;
  }

  .table-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.v1.colors.text.text};
    padding: ${({ theme, compressed }) => (compressed ? theme.v1.size['0.5'] : theme.v1.size['2'])}px;
    border-bottom: 1px solid ${({ theme }) => theme.v1.colors.border.default};
    pointer-events: auto;
    flex-shrink: 0;
    flex-grow: 1;
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    background-color: ${({ theme }) => theme.v1.colors.background.empty};
  }

  .table-cell:first-of-type {
    position: ${({ frozenFirstColumn }) => (frozenFirstColumn ? 'sticky' : 'relative')};
    left: 0;
    background-color: ${({ theme }) => theme.v1.colors.background.empty};
    z-index: 2;
  }
`;

const TableHeaderWrapper = styled.div`
  background-color: ${({ theme }) => theme.v1.colors.background.empty};
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  width: 100%;
  min-width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.v1.colors.border.default};
`;

const VirtualizedRowsWrapper = styled.div<{ height: number }>`
  height: ${({ height, theme }) =>
    height + theme.v1.size['12']}px; // Add additional height to ensure scrollbar doesn't overlap on single-row tables
  width: 100%;
  min-width: 100%;
  position: relative;
  overflow: scroll;
`;

const CellWrapper = styled.div`
  width: 100%;
  white-space: nowrap;
`;

const HeaderWrapper = styled.div<{ width: number; isFirst?: boolean; frozenFirstColumn?: boolean }>`
  width: ${({ width }) => width}px;
  padding: 0;
  white-space: nowrap;
  background-color: ${({ theme }) => theme.v1.colors.background.empty};
  z-index: ${({ isFirst }) => (isFirst ? 2 : 1)};
  flex-shrink: 0;
  flex-grow: 1;
  position: ${({ isFirst, frozenFirstColumn }) => (isFirst && frozenFirstColumn ? 'sticky' : 'relative')};
  left: ${({ isFirst, frozenFirstColumn }) => (isFirst && frozenFirstColumn ? 0 : 'auto')};
`;

const TableCell = <T extends { id: string }>({ cell, width }: { cell: Cell<T, unknown>; width: number }) => (
  <div key={cell.id} className="table-cell" style={{ width }}>
    {flexRender(cell.column.columnDef.cell, cell.getContext())}
  </div>
);

const TableRow = <T extends { id: string }>({ row, virtualRow }: { row: Row<T>; virtualRow: VirtualItem }) => (
  <div
    key={row.id}
    className="table-row"
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      height: `${virtualRow.size}px`,
      transform: `translateY(${virtualRow.start + 32}px)`, // Extra height is to account for the header row
      width: '100%',
    }}
  >
    {row.getVisibleCells().map((cell: Cell<T, unknown>) => (
      <TableCell key={cell.id} cell={cell} width={cell.column.getSize()} />
    ))}
  </div>
);

const MemoizedTableRow = memo(TableRow);

const TableHead = <T extends { id: string }>({
  columns,
  frozenFirstColumn,
  table,
}: {
  columns: ExtendedTableColumn<T>[];
  frozenFirstColumn: boolean | undefined;
  table: ReactTable<T>;
}) => {
  return (
    <TableHeaderWrapper>
      {table.getHeaderGroups().map((headerGroup) => (
        <React.Fragment key={headerGroup.id}>
          {headerGroup.headers.map((header, headerIndex) => {
            const column = columns.find((col) => 'column_' + col.id === header.id);
            if (!column) return null;
            return (
              <HeaderWrapper
                width={header.getSize()}
                key={header.id}
                isFirst={headerIndex === 0}
                frozenFirstColumn={frozenFirstColumn}
              >
                <TableHeader
                  column={{
                    ...column,
                    ...(column.sortPredicate && {
                      isSortActive: header.column.getIsSorted() !== false,
                      sortDirection: header.column.getIsSorted() === 'desc' ? 'descending' : 'ascending',
                      sortCallback: (direction) => {
                        header.column.toggleSorting(direction === 'descending');
                        column.sortCallback?.(direction);
                      },
                    }),
                  }}
                />
              </HeaderWrapper>
            );
          })}
        </React.Fragment>
      ))}
    </TableHeaderWrapper>
  );
};

const TableBody = <T extends { id: string }>({
  header,
  rows,
  compressed,
  memoized = false,
}: {
  header: React.ReactNode;
  rows: Row<T>[];
  compressed?: boolean;
  memoized?: boolean;
}) => {
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableBodyRef.current,
    estimateSize: () => (compressed ? 32 : 48),
    overscan: 30,
  });

  return (
    <VirtualizedRowsWrapper ref={tableBodyRef} height={rowVirtualizer.getTotalSize()}>
      {header}

      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];
        return memoized ? (
          <MemoizedTableRow key={row.original.id} row={row} virtualRow={virtualRow} />
        ) : (
          <TableRow key={row.original.id} row={row} virtualRow={virtualRow} />
        );
      })}
    </VirtualizedRowsWrapper>
  );
};

const Table = <T extends { id: string }>({
  items,
  columns,
  compressed,
  frozenFirstColumn,
  emptyProps,
  testId,
  memoized = false,
}: TableProps<T>) => {
  const { theme } = useTectonTheme();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columnHelper = createColumnHelper<T>();

  const columnWidthMap: Record<TableColumnWidth, string> = {
    s: `${theme.v1.size['24']}px`,
    xs: `${theme.v1.size['40']}px`,
    m: `${theme.v1.size['48']}px`,
    l: `${theme.v1.size['64']}px`,
    xl: `${theme.v1.size['96']}px`,
  };

  const tableColumns = columns.map((column) => {
    return columnHelper.accessor((row) => row, {
      id: 'column_' + column.id,
      header: ({ column: headerColumn }) => (
        <TableHeader
          column={{
            ...column,
            ...(column?.sortPredicate && {
              isSortActive: headerColumn.getIsSorted() !== false,
              sortDirection: headerColumn.getIsSorted() === 'desc' ? 'descending' : 'ascending',
              sortCallback: (direction) => {
                headerColumn.toggleSorting(direction === 'descending');
                column.sortCallback?.(direction);
              },
              width: column.columnWidth ? parseInt(columnWidthMap[column.columnWidth]) : parseInt(columnWidthMap.m),
            }),
          }}
        />
      ),
      cell: (info) => <CellWrapper>{column.content(info.row.original)}</CellWrapper>,
      size: column.columnWidth ? parseInt(columnWidthMap[column.columnWidth]) : parseInt(columnWidthMap.m),
    });
  });

  const table = useReactTable({
    data: items,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  return (
    <TableWrapper
      ref={tableContainerRef}
      compressed={compressed}
      frozenFirstColumn={frozenFirstColumn}
      {...(testId && { 'data-testid': testId })}
      data-role="table"
    >
      <TableBody
        rows={rows}
        compressed={compressed}
        memoized={memoized}
        header={<TableHead columns={columns} frozenFirstColumn={frozenFirstColumn} table={table} />}
      />
      {items.length === 0 && emptyProps && <Empty {...emptyProps} />}
    </TableWrapper>
  );
};

export default Table;
